<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header
        variant="buttons"
        :saving="loading"
        @cancel="closeModal()"
        @submit="save"
      >
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <div class="mt-8">
      <form-card :title="$t(tKey + '.general.title')">
        <div class="flex flex-col gap-y-5">
          <InputField
            v-model="rawPointReward.name"
            :error="errors?.name"
            type="text"
            :label="$t('pages.pointReward.name')"
            :placeholder="$t('pages.pointReward.name')"
          />
          <div class="flex gap-x-5">
            <Switch v-model="rawPointReward.active" :error="errors?.active" :label="$t('pages.pointReward.active.title')" />
            <Switch v-model="rawPointReward.promoted" :error="errors?.promoted" :label="$t('pages.pointReward.promoted.title')" />
          </div>
          <InputField
            v-model="rawPointReward.cost"
            :error="errors?.cost"
            type="number"
            :label="$t('pages.pointReward.cost')"
            :placeholder="$t('pages.pointReward.cost')"
          />
          <InputField
            v-model="rawPointReward.initialStock"
            :error="errors?.initialStock"
            type="number"
            :label="$t('pages.pointReward.initialStock')"
            :placeholder="$t('pages.pointReward.initialStock')"
          />
          <InputField
            v-model="rawPointReward.userQuota"
            :error="errors?.userQuota"
            type="number"
            :label="$t('pages.pointReward.userQuota')"
            :placeholder="$t('pages.pointReward.userQuota')"
          />
          <small class="mb-5">
            {{ $t('pages.pointReward.userQuotaHelp') }}
          </small>
          <div class="flex gap-x-5">
            <DatePicker
              v-model="rawPointReward.validityStart"
              :error="errors?.validityStart"
              mode="datetime"
              class="w-full"
              :label="$t('pages.pointReward.validityStart')"
            />
            <DatePicker
              v-model="rawPointReward.validityEnd"
              :error="errors?.validityEnd"
              mode="datetime"
              class="w-full"
              :label="$t('pages.pointReward.validityEnd')"
            />
          </div>

          <Field :label="$t('pages.pointReward.image')">
            <Box :aspect="1" class="w-full">
              <Medium
                v-model="pointRewardImage.upload.file"
                class="h-full"
                :label="$t('pages.pointReward.image')"
                :icon="PhotoIcon"
                :aspect="1"
                @click="pointRewardImage.upload.open"
              />
            </Box>
          </Field>
          <Switch v-model="rawPointReward.imageEnabled" :label="$t('pages.pointReward.imageEnabled')" />
          <Field :label="$t('pages.pointReward.previewImage')">
            <Box :aspect="1" class="w-full">
              <Medium
                v-model="pointRewardPreviewImage.upload.file"
                class="h-full"
                :label="$t('pages.pointReward.previewImage')"
                :icon="PhotoIcon"
                :aspect="1"
                @click="pointRewardPreviewImage.upload.open"
              />
            </Box>
          </Field>
          <Switch v-model="rawPointReward.previewImageEnabled" :label="$t('pages.pointReward.imageEnabled')" />
          <MultiselectTags
            v-model="rawPointReward.regions"
            :label="$t('pages.pointReward.regions')"
            :options="regionOptions"
            :object="false"
          />
          <InputField
            v-model="rawPointReward.orderIndex"
            :error="errors?.orderIndex"
            type="number"
            :label="$t('pages.pointReward.orderIndex.title')"
          />
        </div>
      </form-card>
      <form-card>
        <template #title>
          {{ $t(tKey + '.translated.title') }}
        </template>
        <translation-accordion v-slot="{ translation }" v-model="rawPointReward" :errors="errors">
          <div class="flex flex-col gap-y-5">
            <InputField
              v-model="translation.title"
              :error="getTranslationError(translation.language)"
              type="text"
              :label="$t('pages.pointReward.title')"
            />
            <TextArea
              v-model="translation.shortDescription"
              :error="getTranslationError(translation.language)"
              :label="$t('pages.pointReward.shortDescription')"
            />
            <HtmlEditor
              v-model="translation.description"
              :error="getTranslationError(translation.language)"
              name="description"
              :label="$t('pages.pointReward.description')"
            />
          </div>
        </translation-accordion>
      </form-card>
    </div>
  </modal>
</template>

<script lang="ts" setup>
import { computed, reactive, ref } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import {
  date, number, object, string, array, boolean,
} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import DatePicker from '@components/utils/inputs/DatePicker.vue';
import InputField from '@utils/inputs/Input.vue';
import TextArea from '@utils/inputs/TextArea.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import Field from '@/components/utils/inputs/Field.vue';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import FormCard from '@utils/FormCard.vue';
import Box from '@utils/Box.vue';
import Medium from '@utils/inputs/Medium.vue';
import Switch from '@utils/inputs/Switch.vue';
import PhotoIcon from '@images/photo.svg?component';
import TranslationAccordion from '@utils/language/TranslationAccordion.vue';
import HtmlEditor from '@utils/inputs/HtmlEditor.vue';
import { syncRef } from '@hooks/state';
import MultiselectTags from '@utils/MultiselectTags.vue';
import { collOptionsProvider } from '@utils/table/hooks/provider';
import type {
  PointReward, PointRewardTranslation, WbFile, Region,
} from '@/entities/Entities';
import { Upload, useSingleFileUpload } from '@/components/utils/upload/fileUpload';

interface Props {
  open: boolean;
  pointReward?: PointReward;
}

const emit = defineEmits(['update:open', 'update:pointReward', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalPointReward = useVModel(props, 'pointReward', emit);

const isSubmitting = ref(false);

const isNew = computed(() => !props.pointReward);
const tKey = computed(() => (isNew.value ? 'pages.pointRewardList.modal.new' : 'pages.pointRewardList.modal.edit'));

const { t } = useI18n();

const notify = useNotify();

const initialData = (pointReward?: PointReward) => {
  const data = pointReward
    ? {
      ...pointReward,
      translations: [...pointReward.translations],
      regions:
          pointReward.regions && pointReward.regions.length
            ? Array.from(pointReward.regions).map((region: Region) => region['@id'] ?? region.id)
            : [],
    }
    : {
      name: '',
      cost: null,
      previewImage: null,
      previewImageEnabled: true,
      image: null,
      imageEnabled: true,
      validityStart: null,
      validityEnd: null,
      initialStock: null,
      userQuota: null,
      orderIndex: null,
      promoted: false,
      active: false,
      translations: [
        {
          title: '',
          shortDescription: '',
          description: '',
          language: 'de',
        },
      ],
      regions: [],
    };
  data.regions = Array.isArray(data.regions) ? data.regions : [];
  return data;
};

const rawPointReward = syncRef(internalPointReward, (pointReward) => ({
  ...initialData(pointReward),
}));

const regionOptions = collOptionsProvider<Region>('/regions', {}, (region: Region) => ({
  label: region.name,
  value: region['@id'],
}));

const getTranslationError = (language: string) => {
  if (!errors?.value?.translations) return undefined;

  if (typeof errors.value.translations === 'string') {
    return errors.value.translations;
  }

  const translationIndex = rawPointReward.value.translations.findIndex((t) => t.language === language);

  return errors.value.translations[translationIndex]?.title;
};

const {
  data: outputPointReward,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    translations: array(
      object({
        title: string().nullable(),
        shortDescription: string().nullable(),
        description: string().nullable(),
        language: string().required(),
      }),
    ).test(
      'has-complete-translation',
      t('validation.translationRequired'),
      (translations) =>
        translations.some((trans) => {
          const hasAnyContent = trans.title || trans.shortDescription || trans.description;
          if (hasAnyContent) {
            return trans.title && trans.shortDescription && trans.description;
          }
          return true;
        }) && translations.some((trans) => trans.title && trans.shortDescription && trans.description),
    ),
    name: string().required(t('validation.notEmpty')),
    cost: number()
      .typeError(t('validation.number'))
      .positive(t('validation.positive'))
      .integer(t('validation.integer'))
      .required(t('validation.notEmpty')),
    initialStock: number().typeError(t('validation.number')).positive(t('validation.positive')).integer(t('validation.integer'))
      .nullable(),
    validityStart: date().nullable(),
    validityEnd: date().nullable(),
    active: boolean().required(t('validation.notEmpty')),
    promoted: boolean().required(t('validation.notEmpty')),
  }),
  rawPointReward,
);

const prevFile = computed(() => (props.pointReward && props.pointReward.image ? Upload.from(props.pointReward.image) : undefined));

const prevPreviewFile = computed(() =>
  (props.pointReward && props.pointReward.previewImage ? Upload.from(props.pointReward.previewImage) : undefined));

const pointRewardImage = reactive({
  key: 'image',
  name: t('pages.pointReward.image'),
  upload: useSingleFileUpload<WbFile>(prevFile),
});

const pointRewardPreviewImage = reactive({
  key: 'previewImage',
  name: t('pages.pointReward.previewImage'),
  upload: useSingleFileUpload<WbFile>(prevPreviewFile),
});

function resetData() {
  rawPointReward.value = initialData(props.pointReward);
  setValidation(false);
  pointRewardImage.upload.reset();
  pointRewardPreviewImage.upload.reset();
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalPointReward.value = undefined;
}

const [exec, { loading: saving }] = useFetchAction();

const loading = computed(() => saving.value || pointRewardImage.upload.loading.value);

const save = validatedFn(async () => {
  if (!outputPointReward.value || isSubmitting.value) {
    return;
  }

  try {
    isSubmitting.value = true;

    const edit = !!props.pointReward;
    const url = edit ? props.pointReward['@id'] : '/point_rewards';

    const uploadFiles = pointRewardImage.upload.getUpdatedFiles(await pointRewardImage.upload.uploadDiff());
    const uploadPreviewFiles = pointRewardPreviewImage.upload.getUpdatedFiles(await pointRewardPreviewImage.upload.uploadDiff());

    const regionsPayload = outputPointReward.value.regions;
    const regionIris = Array.isArray(regionsPayload) ? regionsPayload.map((region) => (typeof region === 'string' ? region : region['@id'])) : [];
    const regionsValue = regionsPayload == null ? [] : [...regionIris];

    const savedPointReward: PointReward = await exec(url, {
      method: edit ? 'PUT' : 'POST',
      body: {
        name: outputPointReward.value.name,
        cost: outputPointReward.value.cost,
        initialStock: outputPointReward.value.initialStock ?? null,
        userQuota: outputPointReward.value.userQuota ?? null,
        validityStart: outputPointReward.value.validityStart,
        validityEnd: outputPointReward.value.validityEnd,
        previewImage: uploadPreviewFiles?.[0]?.file?.['@id'] ?? null,
        previewImageEnabled: outputPointReward.value.previewImageEnabled ?? false,
        image: uploadFiles?.[0]?.file?.['@id'] ?? null,
        imageEnabled: outputPointReward.value.imageEnabled ?? false,
        orderIndex: outputPointReward.value.orderIndex ?? 0,
        active: outputPointReward.value.active ?? false,
        promoted: outputPointReward.value.promoted ?? false,
        translations: (outputPointReward.value.translations as PointRewardTranslation[])
          .filter((trans) => trans.title || trans.shortDescription || trans.description)
          .map((trans) => ({
            ...trans,
            id: trans['@id'] ?? undefined,
          })),
        regions: regionsValue,
      },
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey.value}.saveSuccess`),
    });

    emit('saved', savedPointReward.id);
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('saveError'),
    });
  } finally {
    isSubmitting.value = false;
  }
});
</script>

